import React, { useEffect } from 'react';
import Assets from 'imports/assets.import';
import { convertImageUrlToFile, getApiErrorMessage, getStoredUser, toastifyError, toastifySuccess, useSetState, withSavedProjectDataIds } from 'utils/functions.utils';
import { useNavigate } from 'react-router-dom';
import { clearSession } from 'utils/session.utils';
import {
  Colors,
  FontFamily,
  Models,
  PrimaryButton,
  SaveProjectProgressPopup,
} from 'utils/imports.utils';
import NavBarProfilePopup from 'components/nav_bar_profile_popup/nav_bar_profile_popup.component';
import UndoRedoButtons from 'components/undo_redo_buttons/undo_redo_buttons.component';
import ProfileAvatar from 'common_components/ui/profile_avatar/profile_avatar.ui';
import { ButtonSize, ButtonTypes } from 'utils/interface.utils';
import { RootState } from 'store/store';
import { STRINGS } from 'constants/strings.constant';
import { BreadcrumbItem } from 'interfaces/nav_bar.interface';
import { useSelector, useDispatch } from 'react-redux';
import { compileFiles } from 'utils/compiler';
import { setTargetImageDataList, markSaved, clearHistory } from 'store/reducers/editorSlice';
import UnsavedChangesModal from 'components/unsaved_changes_modal/unsaved_changes_modal.component';
import './nav_bar.component.scss';
interface INavBar {
  logoSize?: any;
  profileData?: any;
  onClick?: () => void;
  isRenderOnlyLogo?: boolean;
  isEditor?: boolean;
  // A path to show beside the logo on a screen that is not the editor; the editor builds its own from the project.
  breadcrumbs?: BreadcrumbItem[];
}
const NavBar = (props: INavBar) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [state, setState] = useSetState({
    progress:0,
    isProfileSettingsPopupOpen: false,
    profileData: null,
    isPreviewModalOpen: false,
    editedProjectName: '',
    isPreviewLoading: false,
    isSavingProject: false,
    projectId: null,
    isProgressPopupOpen: false,
    isUnsavedModalOpen: false,
    // How the editor is being left: the Projects pill / logo, or the browser's back button.
    exitVia: 'projects' as 'projects' | 'history',
  });

  const currentProject = useSelector(
    (state: RootState) => state.project.currentProject,
  );

  const { targetImageDataList, hasUnsavedChanges } = useSelector((state: RootState) => state.editor);

  // Marks the extra history entry that lets the editor see the browser's back button (BrowserRouter
  // has no useBlocker): back lands on the entry below it, and the popstate handler decides what to do.
  const GUARD_STATE_KEY = 'picpopEditorGuard';
  const pushGuardEntry = () => {
    window.history.pushState({ ...window.history.state, [GUARD_STATE_KEY]: true }, '');
  };

  // The one exit from the editor to the projects list (the "Projects" pill and the editor logo).
  const handleBackToProjects = () => {
    if (hasUnsavedChanges) {
      setState({ isUnsavedModalOpen: true, exitVia: 'projects' });
      return;
    }
    navigate('/welcome');
  };

  const leaveEditor = (exitVia: 'projects' | 'history') => {
    setState({ isUnsavedModalOpen: false });
    if (exitVia === 'history') navigate(-1);
    else navigate('/welcome');
  };

  const handleSaveAndLeave = async () => {
    const isSaved = await saveProjectData();
    if (isSaved) {
      leaveEditor(state.exitVia);
      return;
    }
    // The save failed (the error toast already explains why): close the modal and stay in the editor.
    handleKeepEditing();
  };

  const handleDiscardAndLeave = () => {
    dispatch(clearHistory());
    dispatch(markSaved());
    leaveEditor(state.exitVia);
  };

  const handleKeepEditing = () => {
    setState({ isUnsavedModalOpen: false });
    // Browser back already consumed the guard entry; put it back so the next back is caught too.
    if (state.exitVia === 'history') pushGuardEntry();
  };

  const handleLogout = async (e: any) => {
    e.stopPropagation();
    try {
      await Models.auth.logout();
    } catch {
      // Best effort: the user must be signed out locally even when the server can't be reached,
      // and an already-expired session has nothing left to revoke.
    }
    clearSession();
    navigate('/login');
    toastifySuccess('Logout successfully!');
  };

  const compileTargetImages = async () => {
    try {
      // Inside the try: a dead target image URL is a compile failure, not a save failure.
      const listOfFiles = await Promise.all(targetImageDataList.map(async(targetImageData) => (convertImageUrlToFile(targetImageData.targetImage))))
      const targetImageIds = targetImageDataList.map((targetImageData) => targetImageData.id);
      const manifests = await compileFiles([...listOfFiles], targetImageIds, state.projectId ?? '', (progress:number) => {
        setState({progress})
      })

      const updatedTargetImageDataList = targetImageDataList.map((targetImageData) => {
        const matchingManifest = manifests.find((manifest) => manifest.metadata?.targetImageId === targetImageData.id);
        return matchingManifest ? { ...targetImageData, targetId: matchingManifest.target_id } : targetImageData;
      });
      dispatch(setTargetImageDataList(updatedTargetImageDataList));

      return { compiledTargetImageFile: JSON.stringify(manifests), updatedTargetImageDataList };
    } catch (error) {
      toastifyError(getApiErrorMessage(error, 'Failed to compile target images'));
      console.error(error);
      return null;
    }
  }

  const saveProjectData = async (usePreviewLoadingState = false): Promise<boolean> => {
    if (!state.projectId) return false;
    for (const targetImageData of targetImageDataList) {
      if (targetImageData.objects.length === 0) {
        toastifyError('Please add at least one object to the target image');
        return false;
      }
    }
    try {
      setState({ [usePreviewLoadingState ? 'isPreviewLoading' : 'isSavingProject']: true, isProgressPopupOpen: true });
      const compileResult = await compileTargetImages();
      // compileTargetImages has already shown the compile error. Saving anyway would send
      // `complied: null`, which the API rejects with a confusing "complied must be a string".
      if (!compileResult) {
        setState({ [usePreviewLoadingState ? 'isPreviewLoading' : 'isSavingProject']: false, progress: 0, isProgressPopupOpen: false });
        return false;
      }
      console.log("compiledTargetImageFile", compileResult.compiledTargetImageFile);
      const res: any = await Models.auth.saveProjectData({
        project_id: state.projectId,
        data: compileResult.updatedTargetImageDataList,
        complied: compileResult.compiledTargetImageFile,
      });
      if (res?.data) {
        // Save recreated the project_data rows with new ids; keep the editor's ids current so a later
        // object delete edits the live row.
        dispatch(setTargetImageDataList(withSavedProjectDataIds(compileResult.updatedTargetImageDataList, res.data)));
        dispatch(markSaved());
        toastifySuccess('Project Data saved');
        setState({ [usePreviewLoadingState ? 'isPreviewLoading' : 'isSavingProject']: false, progress: 0, isProgressPopupOpen: false });
        if (usePreviewLoadingState) navigate(`/preview/${state.projectId}`);
        return true;
      }
      setState({ [usePreviewLoadingState ? 'isPreviewLoading' : 'isSavingProject']: false, progress: 0, isProgressPopupOpen: false });
      return false;
    } catch (error) {
      setState({ [usePreviewLoadingState ? 'isPreviewLoading' : 'isSavingProject']: false, progress: 0, isProgressPopupOpen: false });
      toastifyError(getApiErrorMessage(error, 'Failed to save project data'));
      return false;
    }
  };
  
  // Editor only: arm the browser-back guard once per history entry.
  useEffect(() => {
    if (!props.isEditor) return;
    if (!window.history.state?.[GUARD_STATE_KEY]) pushGuardEntry();
  }, [props.isEditor]);

  useEffect(() => {
    if (!props.isEditor) return;
    const handlePopState = (event: PopStateEvent) => {
      // Arriving on the guard entry itself (browser forward) is not a request to leave.
      if (event.state?.[GUARD_STATE_KEY]) return;
      if (hasUnsavedChanges) {
        setState({ isUnsavedModalOpen: true, exitVia: 'history' });
        return;
      }
      navigate(-1);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [props.isEditor, hasUnsavedChanges]);

  useEffect(() => {
    setState({ profileData: getStoredUser() });
  }, []);

  useEffect(() => {
    if (currentProject?.project_name && currentProject?.project_id) {
      setState({ editedProjectName: currentProject.project_name, projectId: currentProject.project_id });
    }
  }, [currentProject]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const profilePopup = document.querySelector('.nav_bar_profile_popup');
      const profileImg = document.querySelector('.nav_bar_actions_profile_edit_img_container');

      if (profilePopup && profileImg && !profilePopup.contains(event.target as Node) && !profileImg.contains(event.target as Node)) {
        setState({ isProfileSettingsPopupOpen: false });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <React.Fragment>
      {props.isRenderOnlyLogo ? (
        <div className="nav_bar_only_logo">
          <div className="nav_bar_only_logo_container">
            <img
              src={Assets.logo}
              alt={STRINGS.brand.logoAlt}
              style={{
                width: props.logoSize.width,
                height: props.logoSize.height,
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      ) : (
        <div className={`nav_bar ${props.isEditor && 'nav_bar_editor'}`}>
          <div className="nav_bar_container">
            <div className="nav_bar_left_container">
              <div className="nav_bar_logo_container">
                <div
                    className="nav_bar_logo_img_container"
                    onClick={() => (props.isEditor ? handleBackToProjects() : navigate('/'))}
                  >
                  <img
                    className="nav_bar_logo_img"
                    src={Assets.logo_cream}
                    alt={STRINGS.brand.logoAlt}
                    style={{
                      width: props.logoSize.width,
                      height: props.logoSize.height,
                    }}
                  />
                </div>
              </div>
              {!props.isEditor && props.breadcrumbs && props.breadcrumbs.length > 0 && (
                <div className="nav_bar_path">
                  <div className="nav_bar_path_container">
                    {props.breadcrumbs.map((item, index) => (
                      <React.Fragment key={`${item.label}-${index}`}>
                        {index > 0 && (
                          <div className="nav_bar_path_slash_container">
                            <img src={Assets.path_slash} alt="Path Slash" />
                          </div>
                        )}
                        {item.path ? (
                          <div className="nav_bar_path_item" onClick={() => navigate(item.path ?? '/')}>{item.label}</div>
                        ) : (
                          <div className="nav_bar_path_item nav_bar_path_item_current">{item.label}</div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
              {props.isEditor && (
                <div className="nav_bar_path">
                  <div
                    className="nav_bar_back_button"
                    role="button"
                    tabIndex={0}
                    aria-label={STRINGS.navBar.backToProjects}
                    onClick={handleBackToProjects}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleBackToProjects();
                    }}
                  >
                    <img className="nav_bar_back_button_icon" src={Assets.arrow_left_light} alt="" />
                    <span>{STRINGS.navBar.projects}</span>
                  </div>
                  <div className="nav_bar_path_container">
                    <div className="nav_bar_path_project_name_input_container">
                      <div className="nav_bar_path_project_name_input_container_name">{state?.editedProjectName || 'unknown project'}</div>
                      
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="nav_bar_actions_container">
              {props.isEditor ? (
                <div className="nav_bar_actions_buttons_container">
                  <UndoRedoButtons />
                  <div className="nav_bar_actions_buttons_preview">
                    <PrimaryButton
                      text="Save"
                      fontFamily={FontFamily.medium}
                      type={ButtonTypes.OUTLINED}
                      size={ButtonSize.MEDIUM}
                      style={{border: `1px solid ${Colors.cream}`, borderRadius:'999px', color: Colors.cream}}
                      onClick={() => {
                        if (state.projectId) {
                          saveProjectData();
                        }
                      }}
                      activity={state.isSavingProject}
                    />
                  </div>
                  <div className="nav_bar_actions_buttons_publish">
                    <PrimaryButton
                      text="Preview"
                      fontFamily={FontFamily.medium}
                      type={ButtonTypes.PRIMARY_GRADIENT}
                      size={ButtonSize.MEDIUM}
                      onClick={async () => {
                        await saveProjectData(true);
                      }}
                      activity={state.isPreviewLoading}
                    />
                  </div>
                </div>
              ) : null}
              <div
                className="nav_bar_actions_profile_edit_img_container"
                onClick={() =>
                  setState({
                    isProfileSettingsPopupOpen:
                      !state.isProfileSettingsPopupOpen,
                  })
                }>
                {/* The loaded profile is authoritative; the login-time copy only covers until it arrives. */}
                <ProfileAvatar
                  pictureUrl={props.profileData?.profile_picture}
                  name={(props.profileData ?? state.profileData)?.username}
                />
              </div>
            </div>
            {props.isEditor && state.isUnsavedModalOpen && (
              <UnsavedChangesModal
                isOpen={state.isUnsavedModalOpen}
                isSaving={state.isSavingProject}
                onSave={handleSaveAndLeave}
                onDiscard={handleDiscardAndLeave}
                onClose={handleKeepEditing}
              />
            )}
            {state.isProgressPopupOpen && (
              <SaveProjectProgressPopup isOpen={state.isProgressPopupOpen} progress={state.progress} />
            )}
            {state.isProfileSettingsPopupOpen && (
              <NavBarProfilePopup
                isAdmin={state?.profileData?.role === 'admin'}
                name={(props.profileData ?? state.profileData)?.username}
                email={(props.profileData ?? state.profileData)?.email}
                pictureUrl={(props.profileData ?? state.profileData)?.profile_picture}
                onNavigate={(path) => navigate(path)}
                onLogout={handleLogout}
              />
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default NavBar;
